import { makeVar, useReactiveVar } from '@apollo/client';
import type {
  ShoppingCart,
  ShoppingCartItem,
  UpdateShoppingCartOptions,
} from '../../../types/shoppingCart/ShoppingCart';
import useConvertBidOptionToCartItem from './useConvertBidOptionToCartItem';
import type BidOption from '../../../types/upgrades/bidUpgrade/BidOption';
import type { TokenSegment } from '../../libs/utils/experience/useTokenDataQuery';
import useConvertVariantToShoppingCartItem, {
  type UseConvertVariantToShoppingCartItemOptions,
} from './useConvertVariantToShoppingCartItem';
import { PaymentOption, PurchaseMode, type Variant } from '../../../types/CoreShoppingExperience';
import type DeepPartial from '../../../types/DeepPartial';
import type UpgradeType from '../../../types/airline/UpgradeType';

const initialState = {
  items: [],
  initialized: false,
};

const shoppingCart = makeVar<ShoppingCart>(initialState);

const getShoppingCartItems = () => shoppingCart().items;
const setItems = (items: ShoppingCartItem[]) => shoppingCart({ ...shoppingCart(), items });

/**
 * this function is exported for tests purposes only and shouldn't be used directly in production code
 */
export const resetShoppingCart = () => shoppingCart(initialState);

/**
 * this function is exported for tests purposes only and shouldn't be used directly in production code
 */
export const setShoppingCart = (shoppingCartValue: ShoppingCart) => shoppingCart(shoppingCartValue);

/**
 * this function is exported for tests purposes only and shouldn't be used directly in production code
 */
export const getShoppingCart = () => shoppingCart();

const except = (item: Pick<ShoppingCartItem, 'encryptedId'>) => (i: ShoppingCartItem) =>
  i.encryptedId !== item.encryptedId;

const addItemToCart = (item: ShoppingCartItem) => {
  const items = getShoppingCartItems().filter(except(item));

  setItems([...items, item]);
};

const recursiveMatch = <T extends object>(object: T, query: DeepPartial<T>): boolean =>
  Object.keys(query).every((key) => {
    const keyName: keyof T = key as keyof T;
    return typeof object[keyName] === 'object' && typeof query[keyName] === 'object'
      ? recursiveMatch(object[keyName] as object, query[keyName] as object)
      : object[keyName] === query[keyName];
  });

const getSubmissionOptions = () => shoppingCart().submissionOptions ?? {};

const useShoppingCart = () => {
  const reactiveShoppingCart = useReactiveVar(shoppingCart);

  const { convertBidOptionToCardItem } = useConvertBidOptionToCartItem().actions;

  const findShoppingCartItem = (query: DeepPartial<ShoppingCartItem>): ShoppingCartItem | undefined =>
    reactiveShoppingCart.items.find((item) => recursiveMatch(item, query));

  const { convertVariantToShoppingCartItem } = useConvertVariantToShoppingCartItem().actions;

  const updateShoppingCartItem = (
    query: DeepPartial<ShoppingCartItem>,
    options: Partial<UpdateShoppingCartOptions>,
  ) => {
    setItems(getShoppingCartItems().map((item) => (recursiveMatch(item, query) ? { ...item, ...options } : item)));
  };

  const removeItemFromCart = (query: DeepPartial<ShoppingCartItem>) =>
    setItems(getShoppingCartItems().filter((item) => !recursiveMatch(item, query)));

  const addVariableToSubmissionOptions = (option: Record<string, unknown>) =>
    shoppingCart({
      ...shoppingCart(),
      submissionOptions: { ...getSubmissionOptions(), ...option },
    });

  const hasItemsWithOnlyGivenPaymentOptionInCart = (paymentOption: PaymentOption) =>
    shoppingCart().items.every((x: ShoppingCartItem) => {
      if (paymentOption === PaymentOption.POINTS) {
        return x.pointsPurchaseOption && (x.cashPurchaseOption?.paymentBreakdown?.amount ?? 0) <= 0;
      }
      return x.cashPurchaseOption && (x.pointsPurchaseOption?.paymentBreakdown?.amount ?? 0) <= 0;
    });

  const hasSomeItemsWithGivenUpgradeTypeInCart = (upgradeType: UpgradeType) =>
    shoppingCart().items.some((item) => item.upgradeType === upgradeType);

  const hasSomeItemsWithoutGivenUpgradeTypeInCart = (upgradeType: UpgradeType) =>
    shoppingCart().items.some((item) => item.upgradeType !== upgradeType);

  const hasItemsWithGivenPurchaseModeInCart = (purchaseMode: PurchaseMode) =>
    shoppingCart().items.some(
      (x: ShoppingCartItem) =>
        x.cashPurchaseOption?.purchaseMode === purchaseMode || x.pointsPurchaseOption?.purchaseMode === purchaseMode,
    );

  const getTypeOfPurchaseModeInCart = () => {
    const instant = hasItemsWithGivenPurchaseModeInCart(PurchaseMode.INSTANT);
    const bid = hasItemsWithGivenPurchaseModeInCart(PurchaseMode.BID);
    if (bid && instant) {
      return `${PurchaseMode.BID}-AND-${PurchaseMode.INSTANT}`;
    } else if (bid) {
      return PurchaseMode.BID;
    } else {
      return PurchaseMode.INSTANT;
    }
  };

  return {
    states: {
      shoppingCart: reactiveShoppingCart,
    },
    actions: {
      resetShoppingCart,
      /**
       * it’s only used in load the shopping cart session and please make sure you have a strong reason to set the entire shopping cart
       */
      setShoppingCart,
      addItemToCart,
      addPaymentInfoToAllCartItems: (infoToAdd: (item: ShoppingCartItem) => Partial<ShoppingCartItem>) =>
        setItems(
          getShoppingCartItems().map((item) => {
            return { ...item, ...infoToAdd(item) };
          }),
        ),
      removeItemFromCart,

      updateShoppingCartItem,
      /**
       * @deprecated
       */
      addBidOptionToCart: (bidOption: BidOption, equipmentInfo: TokenSegment | undefined, amount: number) => {
        addItemToCart(convertBidOptionToCardItem(bidOption, equipmentInfo, amount));
      },
      addVariantToCart: (variant: Variant, options: UseConvertVariantToShoppingCartItemOptions) => {
        addItemToCart(convertVariantToShoppingCartItem(variant, options));
      },
      addVariableToSubmissionOptions,
      setInitialization: () => shoppingCart({ ...shoppingCart(), initialized: true }),
    },
    selectors: {
      getShoppingCart, // it’s only used in load the shopping cart session and please make sure you have a strong reason to get the entire shopping cart
      getShoppingCartItems,
      findShoppingCartItem,
      isInitialized: () => shoppingCart().initialized,
      getSubmissionOptions,
      hasItemsWithOnlyGivenPaymentOptionInCart,
      hasSomeItemsWithGivenUpgradeTypeInCart,
      hasSomeItemsWithoutGivenUpgradeTypeInCart,
      getTypeOfPurchaseModeInCart,
    },
  };
};

export default useShoppingCart;
